import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { contractorAuth } from '../lib/contractorAuth';
import './TicketAttachments.css';
import { fmtDate } from '../lib/when';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Mirrors the server's own cap, so the form refuses a file the API would
// reject rather than sending 10MB up the wire to be told no.
const MAX_BYTES = 10 * 1024 * 1024;

const size = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const KIND = [
  [/^image\//, 'Image'],
  [/^application\/pdf$/, 'PDF'],
  [/^text\//, 'Text'],
  [/zip|compressed|tar/, 'Archive'],
  [/sheet|excel|csv/, 'Spreadsheet'],
  [/word|document/, 'Document']
];
const kindOf = (mime) => (KIND.find(([re]) => re.test(mime || ''))?.[1]) || 'File';

/**
 * Files on a ticket.
 *
 * Both endpoints behind this used to be stubs - the list returned a hardcoded
 * empty array and the upload returned a fake id without writing anywhere - so
 * a screenshot attached at ticket creation was accepted and discarded. Nothing
 * here shows optimistic state: a row appears once the server has stored it.
 */
/**
 * `canUpload` and `canRemove` are explicit rather than inferred, because the
 * two are not the same permission. A contractor may now put a file on their
 * own ticket - they are the one at the machine - but deleting is still staff
 * only, so somebody adding evidence cannot remove it. A Remove button that
 * always 403s is worse than no button: it reads as a fault in the app rather
 * than as a thing the person is not allowed to do.
 *
 * Both default to true, so the staff ticket page is unchanged.
 */
/**
 * THE CONTRACTOR'S OWN TOKEN, SENT. Every request below used to go out bare
 * and rely on lib/apiClient's interceptor, which only ever attaches the Entra
 * staff token. A contractor has none, so their list request arrived anonymous
 * and ticketAccess answered 401 - "Could not load the files on this ticket" -
 * and every upload failed the same way. An administrator using "View as" has a
 * staff token, so for them the fold worked, which is how it looked enabled
 * while being unusable by the one person it was widened for.
 *
 * contractorAuth() is undefined for staff, so their requests still go through
 * the interceptor exactly as before.
 */
export default function TicketAttachments({ ticketId, canUpload = true, canRemove = true, onChanged = null }) {
  const [files, setFiles] = useState([]);
  // The file whose Remove was pressed once: the second press, on "Yes,
  // remove", deletes it. This was window.confirm(), which a phone shows as a
  // system sheet with the page's address on it.
  const [confirming, setConfirming] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  // Read once per mount rather than per request: the token does not change
  // while a ticket is open, and a fresh object every render would be a fresh
  // dependency for `load` every render.
  const auth = useMemo(() => contractorAuth(), []);

  const load = useCallback(async () => {
    if (!ticketId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/tickets/${ticketId}/attachments`, auth);
      setFiles(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (err) {
      console.error('Could not load attachments:', err);
      setError('Could not load the files on this ticket');
    } finally {
      setLoading(false);
    }
  }, [ticketId, auth]);

  useEffect(() => { load(); }, [load]);

  /**
   * A link cannot carry a bearer token, and the download endpoint needs one
   * from everybody - a contractor's portal token, or a member of staff's
   * Entra token, which the axios interceptor adds. So the bytes are fetched
   * and handed to the browser as an object URL.
   */
  const download = async (file) => {
    try {
      const r = await axios.get(`${API_BASE}/attachments/${file.id}/download`,
        { ...auth, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file_name || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoked on a delay; revoking at once races the download in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      setError(err.response?.data?.error || `Could not download ${file.file_name}`);
    }
  };

  const upload = async (chosen) => {
    setError(null);
    const tooBig = chosen.filter((f) => f.size > MAX_BYTES);
    if (tooBig.length > 0) {
      setError(
        `${tooBig.map((f) => f.name).join(', ')} ${tooBig.length === 1 ? 'is' : 'are'} ` +
        `over the ${MAX_BYTES / 1024 / 1024}MB limit`
      );
    }

    const sendable = chosen.filter((f) => f.size <= MAX_BYTES && f.size > 0);
    if (sendable.length === 0) return;

    setBusy(true);
    const failed = [];
    for (const file of sendable) {
      const body = new FormData();
      body.append('file', file);
      try {
        await axios.post(`${API_BASE}/tickets/${ticketId}/attachments`, body, {
          headers: { ...(auth?.headers || {}), 'Content-Type': 'multipart/form-data' }
        });
      } catch (err) {
        // A settled ticket answers 409 with the reason; a lost session 401.
        // Both are worth more than "upload failed".
        const why = err.response?.data?.error
          || (err.response?.status === 401 ? 'your session has expired' : 'upload failed');
        failed.push(`${file.name} (${why})`);
      }
    }
    setBusy(false);
    if (failed.length > 0) setError(`Did not upload: ${failed.join('; ')}`);
    await load();
    if (onChanged) onChanged();
  };

  const remove = async (file) => {
    setConfirming(null);
    setBusy(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE}/attachments/${file.id}`, auth);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove that file');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ticket-attachments">
      <div className="ta-head">
        <h3>
          Files
          {files.length > 0 && <span className="ta-count">{files.length}</span>}
        </h3>
        {canUpload && (
        <div>
          <input
            ref={inputRef}
            id={`ta-input-${ticketId}`}
            type="file"
            multiple
            className="ta-input"
            onChange={(e) => {
              upload(Array.from(e.target.files));
              // Clear it, so picking the same file twice still fires a change.
              e.target.value = '';
            }}
          />
          <button
            type="button"
            className="btn btn-secondary btn-small"
            disabled={busy}
            onClick={() => inputRef.current && inputRef.current.click()}
          >
            {busy ? 'Uploading…' : 'Attach a file'}
          </button>
        </div>
        )}
      </div>

      {error && <p className="ta-error" role="alert">{error}</p>}

      {loading ? (
        <p className="ta-empty">Loading files…</p>
      ) : files.length === 0 ? (
        <p className="ta-empty">
          No files yet. Screenshots and logs attached here stay with the ticket.
        </p>
      ) : (
        <ul className="ta-list">
          {files.map((f) => (
            <li key={f.id} className="ta-row">
              <span className="ta-kind" aria-hidden="true">{kindOf(f.mime_type)}</span>
              <span className="ta-name">
                {/* A fetch for everybody, not a plain link for staff. Staff
                    sign in with a token in a header too (staff-auth is
                    bearer-only), and a plain link carries no header - so the
                    staff link was answered "Sign in to read this ticket".
                    axios's interceptor signs a staff request; a contractor's
                    request carries their own token. */}
                <button type="button" className="ta-link" onClick={() => download(f)}>
                  {f.file_name}
                </button>
                <span className="ta-meta">
                  {size(f.byte_size)}
                  {f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ''}
                  {f.uploaded_by_contractor ? ' (contractor)' : ''}
                  {f.created_at ? ` · ${fmtDate(f.created_at)}` : ''}
                </span>
              </span>
              {canRemove && confirming !== f.id && (
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  disabled={busy}
                  onClick={() => setConfirming(f.id)}
                  aria-label={`Remove ${f.file_name}`}
                >
                  Remove
                </button>
              )}
              {canRemove && confirming === f.id && (
                <span className="ta-confirm" role="group" aria-label={`Remove ${f.file_name}? This cannot be undone.`}>
                  <span className="ta-confirm-q">Remove it? This cannot be undone.</span>
                  <button type="button" className="btn btn-danger btn-small" disabled={busy}
                          onClick={() => remove(f)}>Yes, remove</button>
                  <button type="button" className="btn btn-secondary btn-small" disabled={busy}
                          onClick={() => setConfirming(null)}>Keep</button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
